import { Fragment } from 'react';
import { getCountryData, ICountryData, TCountryCode } from 'countries-list';
import { country, subdivision } from 'iso-3166-2';
import z from 'zod';

const usZipCodeRegex = /^\d{5}(-\d{4})?$/;

// RE_BUSINESS_ADDRESS: ^[À-ÖØ-öø-ǿa-zA-Z0-9().\-_#,;/@$!% ]{1,}$
const addressLineRegex = /^[À-ÖØ-öø-ǿa-zA-Z0-9().\-_#,;/@$!% ]+$/;

// RE_LOCALITY: ^[0-9a-zA-Z _\-¡-￿]+$
const cityRegex = /^[0-9a-zA-Z _\-¡-￿]+$/;

// RE_POSTAL_CODE: ^[0-9a-zA-Z ]*$
const postalCodeRegex = /^[0-9a-zA-Z ]*$/;

export function getCountryFromCode(code: string): ICountryData | null {
  const data = getCountryData(code as TCountryCode);
  if (!data.name) return null;
  return data;
}

export interface IStateProvince {
  iso2: string;
  name: string;
}

// We only require state/province data for these countries.
export const STATE_PROVINCE_COUNTRIES = new Set(['US', 'CA', 'AU']);
export function hasStateProvinces(countryCode: string): boolean {
  return STATE_PROVINCE_COUNTRIES.has(countryCode.toUpperCase());
}

/**
 * Get a list of states/provinces for a given country.
 */
export function getStateProvincesList(countryCode: string): IStateProvince[] {
  if (!hasStateProvinces(countryCode)) return [];

  const data = country(countryCode.toUpperCase());
  if (!data) return [];

  const subregions = data.sub;
  const list = Object.entries(subregions).map(([fullCode, data]) => {
    const iso2 = fullCode.replace(`${countryCode.toUpperCase()}-`, '');
    return { iso2, name: data.name };
  });
  return list.sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * Get a state/province from a given country and state/province code.
 */
export function getStateProvinceFromCode(
  countryCode: string,
  stateProvinceCode: string
): IStateProvince | null {
  if (!hasStateProvinces(countryCode)) return null;

  const data = subdivision(
    countryCode.toUpperCase(),
    stateProvinceCode.toUpperCase()
  );
  if (!data) return null;

  return { iso2: stateProvinceCode, name: data.name };
}

export const addressSchema = z
  .object({
    line1: z
      .string()
      .min(1, 'Address is required')
      .max(60, 'Address must be 60 characters or less')
      .regex(addressLineRegex, 'Address contains invalid characters'),
    line2: z
      .string()
      .max(60, 'Address must be 60 characters or less')
      .regex(addressLineRegex, 'Address contains invalid characters')
      .optional()
      .or(z.literal('')),
    city: z
      .string()
      .min(1, 'City is required')
      .max(50, 'City must be 50 characters or less')
      .regex(cityRegex, 'City contains invalid characters'),
    postalCode: z
      .string()
      .min(1, 'Postal code is required')
      .max(20, 'Postal code must be 20 characters or less')
      .regex(
        postalCodeRegex,
        'Postal code can only contain letters, numbers, and spaces'
      ),
    country: z
      .string()
      .min(2, 'Country is required')
      .max(2, 'Country is required')
      .refine((country) => !!getCountryFromCode(country), 'Invalid country'),
    state: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    if (!data.country) return;

    const isRequired = hasStateProvinces(data.country);
    if (!isRequired) return;

    if (!data.state) {
      const label = data.country === 'CA' ? 'Province' : 'State';
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `${label} is required`,
        path: ['state'],
      });
    } else if (!getStateProvinceFromCode(data.country, data.state)) {
      const label = data.country === 'CA' ? 'province' : 'state';
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Invalid ${label}`,
        path: ['state'],
      });
    }
  })
  .refine(
    (data) => {
      if (data.country !== 'US') return true;
      if (!data.postalCode) return false;
      return usZipCodeRegex.test(data.postalCode);
    },
    {
      path: ['postalCode'],
      message: 'Invalid zip code',
    }
  );

export interface IAddress {
  line1: string;
  line2?: string;
  city: string;
  postalCode: string;
  country: string;
  state?: string;
}

export function formatAddress(address: IAddress) {
  const countryCode = address.country.toLocaleUpperCase();
  const country = getCountryFromCode(countryCode)?.name ?? countryCode;

  const lines = [address.line1];

  if (address.line2) {
    lines.push(address.line2);
  }

  let cityState = address.city;
  if (address.state) {
    cityState += `, ${address.state.toLocaleUpperCase()}`;
  }
  lines.push(`${cityState} ${address.postalCode}`);

  lines.push(country);

  return lines;
}

interface AddressProps {
  address: IAddress;
}

export function Address({ address }: AddressProps) {
  const lines = formatAddress(address);

  return (
    <>
      {lines.map((line, index) => (
        <Fragment key={index}>
          {line}
          {index < lines.length - 1 && <br />}
        </Fragment>
      ))}
    </>
  );
}
